import React, { useEffect, useState } from 'react'
import { useWidgetSite } from '../../../contexts/WidgetSiteContext'
import { useDebounce } from '../../../hooks'
import { fetchCarouselPreview, PreviewCard } from '../../../services/api'
import formStyles from '../../../styles/forms.module.scss'
import type { CarouselMetadata, CarouselPin } from './CarouselEditor'
import { groupsOf } from './CarouselFilterGroups'
import { dropPin, pinsByTile, pinsNotRendered } from './carouselPins'
import styles from './CardCarouselEditor.module.scss'

interface CarouselPreviewPaneProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
}

// Low-fidelity live preview: posts the in-progress config to the CMS API, which
// proxies to ACE's previewWidgetItems, and renders the resolved sequence as
// plain tiles. Real content and real order, deliberately not the real app's
// styling.
//
// The request carries the pins, so the response is the woven rail rather than
// the dynamic results alone. **The strip renders that response verbatim.** It is
// also the surface pins are placed on: a drag paints a caret showing the order
// it would take, the drop writes the pin's position, and the strip refetches and
// re-renders from ACE. Nothing here reproduces ACE's placement, so the preview
// cannot drift from what the rail will actually show — and nothing here promises
// a landing slot, because where a pin rides up to depends on how much content
// the query supplies (see dropPosition in carouselPins).

// How many slots the strip asks for. A rail with a smaller limit asks for its
// own length — the limit caps the rail, so previewing past it would overstate
// what a viewer sees.
const PREVIEW_SIZE = 20

const PinIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={12}
    height={12}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 17v5" />
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
  </svg>
)

/** What to call a pin with no card of its own to show. */
const pinLabel = (pin: CarouselPin): string => {
  if (pin.type === 'custom') return pin.title || 'Custom card'
  return pin.type === 'match' ? 'Match pin' : 'VOD pin'
}

const CarouselPreviewPane: React.FC<CarouselPreviewPaneProps> = ({ metadata, onChange }) => {
  const { siteId } = useWidgetSite()
  // Only the content-resolving fields matter to ACE; presentation edits (title —
  // Name mirrors into title on every keystroke) must not trigger a fresh preview
  // round-trip.
  //
  // ACE resolves one filter set, so a unioned config can't be previewed whole.
  // Previewing the first group is still useful, but only if the pane says
  // that's what it is — sending no filters at all would render the unfiltered
  // rail and read as a much wider result than the config actually produces.
  const groups = groupsOf(metadata)
  const unioned = groups.length > 1
  const first = groups[0] ?? {}
  const pins = metadata.pins ?? []
  const size = Math.min(metadata.limit ?? PREVIEW_SIZE, PREVIEW_SIZE)
  const configKey = JSON.stringify({
    sources: metadata.sources,
    filter_groups: Object.keys(first).length ? [first] : undefined,
    sort: metadata.sort,
    limit: metadata.limit,
    ended_grace_minutes: metadata.ended_grace_minutes,
    pins: pins.length ? pins : undefined,
  })
  const debouncedKey = useDebounce(configKey, 400)
  const hasSources = ((metadata.sources as string[] | undefined) ?? []).length > 0
  // A fully curated rail is previewable too: its pins are the whole sequence.
  const ready = !!siteId && (hasSources || pins.length > 0)

  // null until the first response, so an empty strip reads as "not loaded yet"
  // rather than "no content". A refetch keeps the previous cards on screen and
  // veils them, so the strip never blinks out between edits. cards and hasNext
  // are one value because pinsNotRendered reads them as a pair.
  const [result, setResult] = useState<{ cards: PreviewCard[]; hasNext: boolean } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Which pin is in flight, and the gap it would drop into. The strip does not
  // reorder while dragging — the caret shows where the card is headed, and ACE
  // decides where it lands.
  const [dragPinId, setDragPinId] = useState<string | null>(null)
  const [caret, setCaret] = useState<number | null>(null)

  // Replacing the strip abandons any drag in flight: a caret indexes the tiles
  // on screen, so against different tiles it means somewhere else. This is also
  // the only thing that frees dragPinId when the dragged tile unmounts here —
  // the veil is pointer-events: none, so a drag can start while an earlier
  // edit's refetch is still running, and dragend never fires for a tile that
  // went away. Left set, it has the strip swallow the next unrelated drop (a
  // file, some text) and write it to that pin.
  //
  // endDrag must stay OUT of the dep array: it is a fresh closure every render,
  // and this effect fetches. exhaustive-deps does not ask for it, and only the
  // stable setters it calls survive the stale closure, which is all we need.
  useEffect(() => {
    if (!ready) {
      setResult(null)
      setError(null)
      endDrag()
      return
    }
    let active = true
    setLoading(true)
    setError(null)
    fetchCarouselPreview(siteId, JSON.parse(debouncedKey), size)
      .then(res => {
        if (!active) return
        setResult({ cards: res.cards, hasNext: res.has_next_page })
        endDrag()
      })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Preview failed') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [debouncedKey, ready, siteId, size])

  const tiles = result?.cards ?? []
  const tilePins = pinsByTile(pins, tiles)
  const notRendered = result ? pinsNotRendered(pins, tilePins, tiles.length, result.hasNext) : []

  // One drag-over handler for the strip, its tiles and its tail. The strip
  // itself takes the handler with no boundary so a drop over the gap between
  // tiles still registers; a boundary moves the caret.
  const allowDrag = (e: React.DragEvent, boundary?: number) => {
    if (!dragPinId) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (boundary !== undefined) setCaret(boundary)
  }

  // Firefox cancels a drag whose dragstart set no dataTransfer payload, so the
  // setData call is what makes pin placement work there at all — the id it
  // carries is unused, since the drop reads dragPinId. effectAllowed pairs with
  // the dropEffect above so the cursor shows a move rather than a copy.
  const startDrag = (e: React.DragEvent, pinId: string) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', pinId)
    setDragPinId(pinId)
  }

  const endDrag = () => {
    setDragPinId(null)
    setCaret(null)
  }

  const commitDrop = () => {
    if (!dragPinId || caret === null) return endDrag()
    const next = dropPin(pins, tilePins, dragPinId, caret, tiles.length)
    endDrag()
    if (next) onChange({ ...metadata, pins: next })
  }

  /** Which gap the cursor is nearest: before this tile, or after it. */
  const caretFor = (e: React.DragEvent, index: number) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    return e.clientX > rect.left + rect.width / 2 ? index + 1 : index
  }

  if (!ready) {
    return <p className={formStyles.hint}>Add a source or a pinned card to preview this rail.</p>
  }

  return (
    <div className={styles.previewPane}>
      <div className={styles.previewHeader}>
        <span>Live preview</span>
        {!hasSources && <span className={styles.previewStatus}>Fully curated</span>}
        {unioned && <span className={styles.previewStatus}>First filter group only</span>}
        <span className={styles.previewStatus}>
          {loading ? 'Refreshing from the server…' : 'As the server resolves it'}
        </span>
      </div>

      {pins.length > 0 && (
        <p className={formStyles.hint}>
          Drag a pinned card to where you want it. The server re-resolves the rail
          on every change, so this strip is always what a viewer would get.
        </p>
      )}

      {error && <p className={styles.previewError}>{error}</p>}
      {!error && !loading && result !== null && tiles.length === 0 && (
        <p className={formStyles.hint}>No matching content for this config.</p>
      )}

      {/* Veiled rather than emptied while the server re-resolves: the cards on
          screen are one edit stale, which is honest, where a blank strip would
          read as "this config matches nothing". */}
      <div className={styles.previewCanvas}>
        <div
          className={styles.previewStrip}
          onDragOver={(e) => allowDrag(e)}
          onDrop={(e) => { e.preventDefault(); commitDrop() }}
        >
          {tiles.map((card, i) => {
            const pin = tilePins[i]
            const dragging = !!pin && pin.id === dragPinId
            const classes = [
              styles.previewCard,
              pin ? styles.previewCardPinned : '',
              dragging ? styles.previewCardDragging : '',
            ].filter(Boolean).join(' ')
            return (
              <React.Fragment key={`${card.cardId}-${i}`}>
                {caret === i && <span className={styles.previewCaret} />}
                <div
                  className={classes}
                  draggable={!!pin}
                  onDragStart={pin ? (e) => startDrag(e, pin.id) : undefined}
                  onDragEnd={endDrag}
                  onDragOver={(e) => allowDrag(e, caretFor(e, i))}
                  title={pin ? 'Drag to move this pinned card' : undefined}
                >
                  {card.image?.cdnUrl ? (
                    <img
                      src={card.image.cdnUrl}
                      alt={card.image.altText ?? card.title}
                      draggable={false}
                    />
                  ) : (
                    <div className={styles.previewThumbEmpty} />
                  )}

                  {/* Slot number, the card's own status badge and the pin glyph
                      share one row so none of them covers another. */}
                  <div className={styles.previewCardMarks}>
                    <span className={styles.previewSlot}>{i + 1}</span>
                    {card.badge && <span className={styles.previewBadge}>{card.badge}</span>}
                    {pin && (
                      <span className={styles.previewPin}>
                        <PinIcon />
                      </span>
                    )}
                  </div>

                  <div className={styles.previewCardTitle}>{card.title}</div>
                  {card.subtitle && <div className={styles.previewCardSub}>{card.subtitle}</div>}
                </div>
              </React.Fragment>
            )
          })}

          {caret === tiles.length && <span className={styles.previewCaret} />}
          {/* Fills the rest of the row so a card can be dropped past the end. */}
          <div
            className={styles.previewStripTail}
            onDragOver={(e) => allowDrag(e, tiles.length)}
          />
        </div>
        {loading && <div className={styles.previewVeil} />}
      </div>

      {notRendered.length > 0 && (
        <div className={styles.previewMissing}>
          <p className={formStyles.hint}>
            Not in the rail — unfinished, or content the server could not resolve:
          </p>
          <div className={styles.previewMissingRow}>
            {notRendered.map(pin => (
              <span key={pin.id} className={styles.previewMissingChip}>
                <PinIcon />
                {pinLabel(pin)}
                <span className={styles.previewMissingSlot}>slot {pin.position + 1}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CarouselPreviewPane
